import {t, Trans} from '@lingui/macro';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import noResultsImageSrc from '../../../../../../../../src/assets/images/noResults.svg';
import {EmailDomainDropdownModesEnum} from '../../../../../../../../src/components/accounts/accountDetails/accountInformation/emailDomainDropdown';
import {EmailDomainDropdownContainer} from '../../../../../../../../src/components/accounts/accountDetails/accountInformation/emailDomainDropdownContainer';
import {SearchContactsDropdownContainer} from '../../../../../../../../src/components/common/dropdowns/search/searchContactsDropdownContainer';
import {computeMultiSelectCheckboxState} from '../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {useAsyncTableSource} from '../../../../../../../../src/components/common/table/helpers/useAsyncTableSource';
import {TableColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {TableMultiSelectHeaderRow} from '../../../../../../../../src/components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {
  VirtualTable,
  VirtualTableHandle,
} from '../../../../../../../../src/components/common/table/virtualTable';
import {
  VirtualTableRowRenderer,
  VirtualTableRowSiblingData,
} from '../../../../../../../../src/components/common/table/virtualTableBase';
import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {globalNamespace} from '../../../../../../../../src/helpers/models/namespaceHelpers';
import {AccountModel} from '../../../../../../../../src/models/api/accountModel';
import {TableOffsetPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {Localizer} from '../../../../../../../../src/store/stateless/internationalizationSelectors';
import {useLocalize} from '../../../../../../../../srcCommon/internationalization/internationalizationContext';
import {KeyboardActionsEnum} from '../../../../../../../shared/helpers/src/browser/keyboardHelpers';
import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../../../../shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../shared/ui/src/components/keyboard/keyboardShortcuts';
import {PopoverCoordinator} from '../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {accountDetailsContactsDropdownRenderTooltip} from '../../helpers/contactsDropdownHelpers';
import {
  getAllTableColumnsInOrder,
  renderContactsTableMultiSelectCardCount,
} from '../common/contacts/contactsTableHelpers';
import {ContactsTableRowContainer} from '../common/contacts/contactsTableRowContainer';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';
import {ContactsManagerTableEmptyState} from '../common/contactsManagerEmptyState';
import {SharedColumnsEnum} from '../common/sharedTableConfig';
import {
  computeBulkSelectedValues,
  computeIsSiblingSelected,
  computeNextSelectedItemId,
  computePreviousSelectedItemId,
} from '../common/tableHelpers';

/*
 * Props.
 */

interface AccountContactsTableProps {
  panelId: ContactsManagerPanelId;
  account: AccountModel;
  dynamicColumns: ReadonlyArray<
    TableColumn<SharedColumnsEnum, ContactsManagerCustomFieldColumnMetadata | undefined>
  >;
  hasDomainsConnected: boolean;
  tableRef: React.MutableRefObject<VirtualTableHandle | undefined>;
  cardIds: ReadonlyArray<number>;
  paginationData?: TableOffsetPaginationDataModel;
  selectedCardIds: ReadonlyArray<number>;
  isInMultiSelectMode: boolean;
  multiSelectHeaderActions: ReactNode;

  onReset: Handler;
  onFetchTableDataAsync: AsyncHandlerOf<CancelToken>;
  addCardToAccount: AsyncHandlerOf<number>;
  onAddSelectedCardIds: (cardIds: ReadonlyArray<number>, shouldForceMultiSelectMode?: boolean) => void;
  onRemoveSelectedCardIds: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 0;
  position: relative;
`;

const StyledImg = styled.img`
  width: 92px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`;

const StyledAddDomainButton = styled(Button)`
  margin-right: 8px;
`;

const StyledActionIcon = styled(Icon)`
  margin-right: 6px;
`;

/*
 * Component.
 */

export const AccountContactsTable: FC<AccountContactsTableProps> = (props) => {
  const {
    panelId,
    account,
    dynamicColumns,
    hasDomainsConnected,
    tableRef,
    cardIds,
    paginationData,
    selectedCardIds,
    isInMultiSelectMode,
    multiSelectHeaderActions,
    onFetchTableDataAsync,
    onReset,
    addCardToAccount,
    onAddSelectedCardIds,
    onRemoveSelectedCardIds,
  } = props;
  const localize = useLocalize();
  const tableColumns = getAllTableColumnsInOrder(dynamicColumns);
  const lastSelectedCardIdRef = useRef<number>();

  const onSelectCardId = (cardId: number) => {
    onAddSelectedCardIds([cardId]);
    lastSelectedCardIdRef.current = cardId;
  };

  const keyboardHandlers: KeyboardEventHandlers = {
    [KeyboardActionsEnum.MOVE_UP]: () => {
      if (isInMultiSelectMode) {
        return;
      }
      const nextSelectedCardId = computePreviousSelectedItemId(cardIds, selectedCardIds);
      if (nextSelectedCardId) {
        onSelectCardId(nextSelectedCardId);
      }
    },
    [KeyboardActionsEnum.MOVE_DOWN]: () => {
      if (isInMultiSelectMode) {
        return;
      }
      const nextSelectedCardId = computeNextSelectedItemId(cardIds, selectedCardIds);
      if (nextSelectedCardId) {
        onSelectCardId(nextSelectedCardId);
      }
    },
  };

  const source = useAsyncTableSource(
    {
      onReset: async () => onReset(),
      fetcher: async ({cancelToken}) => onFetchTableDataAsync(cancelToken),
    },
    [],
  );

  const renderRow: VirtualTableRowRenderer<number> = (
    cardId: number,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<number>,
  ) => (
    <ContactsTableRowContainer
      isSelected={selectedCardIds.includes(cardId)}
      isPreviousSiblingSelected={computeIsSiblingSelected(selectedCardIds, siblingData?.previous)}
      isNextSiblingSelected={computeIsSiblingSelected(selectedCardIds, siblingData?.next)}
      cardId={cardId}
      panelId={panelId}
      options={options}
      tableColumns={tableColumns}
      isInMultiSelectMode={isInMultiSelectMode}
      onSelect={() => onSelectCardId(cardId)}
      onMultiSelect={() => {
        onAddSelectedCardIds(computeBulkSelectedValues(cardId, lastSelectedCardIdRef.current, cardIds), true);
      }}
      onCheckBoxToggle={(shouldSelect) => {
        if (shouldSelect) {
          onAddSelectedCardIds([cardId], true);
          lastSelectedCardIdRef.current = cardId;
        } else {
          onRemoveSelectedCardIds([cardId]);
        }
      }}
    />
  );

  const renderEmpty = () => (
    <ContactsManagerTableEmptyState
      title={<Trans id="account-contacts-table-empty-state-title">No contacts yet</Trans>}
      image={<StyledImg alt="account-contacts-empty-state-image" src={noResultsImageSrc} />}
      description={renderEmptyStateDescription(hasDomainsConnected)}
    >
      {renderEmptyStateButtons(hasDomainsConnected, account, localize, addCardToAccount, tableRef)}
    </ContactsManagerTableEmptyState>
  );

  const renderMultiSelectHeader = (onMultiSelectHeaderChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      onMultiSelectChange={onMultiSelectHeaderChange}
      multiSelectStatus={computeMultiSelectCheckboxState(cardIds.length, selectedCardIds.length)}
      renderTotalItemsLabel={() => renderContactsTableMultiSelectCardCount(selectedCardIds.length)}
    >
      {multiSelectHeaderActions}
    </TableMultiSelectHeaderRow>
  );

  return (
    <StyledTableContainerDiv>
      <KeyboardShortcuts handlers={keyboardHandlers}>
        <VirtualTable<number>
          ref={tableRef}
          columns={tableColumns}
          source={source}
          items={cardIds}
          size={VisualSizesEnum.SMALL}
          paginationData={paginationData}
          renderRow={renderRow}
          renderEmpty={renderEmpty}
          shouldAllowMultiSelect
          isInMultiSelectMode={isInMultiSelectMode}
          isSourceUnstable
          renderMultiSelectHeader={renderMultiSelectHeader}
          onMultiSelectHeaderChange={(checked) => {
            if (checked) {
              onAddSelectedCardIds(cardIds);
            } else {
              onRemoveSelectedCardIds(cardIds);
            }
          }}
        />
      </KeyboardShortcuts>
    </StyledTableContainerDiv>
  );
};

/*
 * Helpers.
 */

const renderEmptyStateDescription = (hasDomainsConnected: boolean) => {
  if (hasDomainsConnected) {
    return (
      <Trans id="account-contacts-table-empty-state-description-domains">
        Contacts with matching email domains for this account will automatically appear here. You can also add
        contacts manually.
      </Trans>
    );
  }
  return (
    <Trans id="account-contacts-table-empty-state-description-no-domains">
      Add domain so that contacts with matching email domains will be automatically associated with this
      account, or add contacts manually.
    </Trans>
  );
};

const renderEmptyStateButtons = (
  hasDomainsConnected: boolean,
  account: AccountModel,
  localize: Localizer,
  addCardToAccount: AsyncHandlerOf<number>,
  tableRef: React.MutableRefObject<VirtualTableHandle | undefined>,
) => (
  <>
    {!hasDomainsConnected && (
      <PopoverCoordinator
        renderButton={(isOpen) => (
          <StyledAddDomainButton variant={ButtonVariantsEnum.PRIMARY} hasHover={isOpen}>
            <StyledButtonContentDiv>
              <StyledActionIcon name="mentionSmall" />
              <Trans id="account-contacts-table-empty-state-action-add-domain">Add domain</Trans>
            </StyledButtonContentDiv>
          </StyledAddDomainButton>
        )}
        renderModal={(popoverProps) => (
          <EmailDomainDropdownContainer
            account={account}
            mode={EmailDomainDropdownModesEnum.ADD}
            popoverTransferProps={popoverProps}
            onCancel={() => popoverProps.onRequestClose()}
            onSave={() => {
              popoverProps.onRequestClose();
              if (tableRef.current) {
                tableRef.current.forceReload();
              }
            }}
            placement="top"
          />
        )}
      />
    )}
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <Button hasHover={isOpen}>
          <StyledButtonContentDiv>
            <StyledActionIcon name="addContactSmall" />
            <Trans id="account-contacts-table-empty-state-action-add-contact">Add contact</Trans>
          </StyledButtonContentDiv>
        </Button>
      )}
      renderModal={(popoverProps) => (
        <SearchContactsDropdownContainer
          popoverProps={popoverProps}
          searchPlaceholder={localize(
            t('account-contacts-table-search-placeholder')`Search for a contact to add`,
          )}
          filterSearchResults={(card) => card.namespace === globalNamespace}
          onConfirm={async (card) => {
            popoverProps.onRequestClose();
            await addCardToAccount(card.id);
          }}
          computeIsContactDisabled={(card) => Boolean(card.accountId)}
          renderTooltip={accountDetailsContactsDropdownRenderTooltip}
        />
      )}
    />
  </>
);
